import { useEffect, useRef, useState } from "react";
import classes from "../homePageNew.module.css";
import { CheckIcon, ChevronDownIcon } from "./icons";

/**
 * Generic combination picker — one styled dropdown per dimension, with
 * options derived from the record's own metrics entries so only real,
 * existing combinations can ever be selected.
 *
 * `dimensions` is an ordered list of { key, label } (e.g. category, market,
 * persona). LLM is deliberately not a dimension for Brand Audit: every LLM
 * for the chosen combination is shown in the score table instead of being
 * selected one at a time.
 *
 * A dimension with only one possible value renders as a static chip rather
 * than a dropdown — nothing to choose, so it shouldn't look interactive.
 * Dropdowns are a custom trigger + menu (not a native <select>) so they can
 * carry the app's own visual language instead of the browser's default.
 *
 * Each dimension's own `format` function (optional) controls DISPLAY text
 * only — the underlying value passed to onChange/matching is always the
 * raw option (e.g. an LLM's raw key), so a dimension can show a friendlier
 * label (llmLabel) without changing what's actually selected/matched.
 */
export default function CombinationSelector({ combinations, value, onChange, dimensions }) {
  const [openKey, setOpenKey] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!openKey) return undefined;
    const handleOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpenKey(null);
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setOpenKey(null);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openKey]);

  const optionsFor = (key) => [...new Set((combinations || []).map((c) => c?.[key]).filter(Boolean))];

  return (
    <div className={classes.comboBar} ref={rootRef}>
      {dimensions.map(({ key, label, format }) => {
        const options = optionsFor(key);
        if (options.length === 0) return null;
        const current = value?.[key] ?? options[0];
        const display = (option) => (format ? format(option) : option);

        if (options.length === 1) {
          return (
            <div className={classes.comboField} key={key}>
              <span className={classes.comboLabel}>{label}</span>
              <span className={classes.comboStatic} title={display(current)}>
                {display(current)}
              </span>
            </div>
          );
        }

        const isOpen = openKey === key;
        return (
          <div className={classes.comboField} key={key}>
            <span className={classes.comboLabel}>{label}</span>
            <button
              type="button"
              className={`${classes.comboTrigger} ${isOpen ? classes.comboTriggerOpen : ""}`}
              onClick={() => setOpenKey(isOpen ? null : key)}
              title={display(current)}
            >
              <span className={classes.comboTriggerValue}>{display(current)}</span>
              <span className={`${classes.comboChevron} ${isOpen ? classes.comboChevronOpen : ""}`}>
                <ChevronDownIcon size={12} />
              </span>
            </button>
            {isOpen && (
              <div className={classes.comboMenu} role="listbox">
                {options.map((option) => {
                  const active = option === current;
                  return (
                    <button
                      type="button"
                      key={option}
                      role="option"
                      aria-selected={active}
                      className={`${classes.comboMenuItem} ${active ? classes.comboMenuItemActive : ""}`}
                      onClick={() => {
                        onChange({ ...value, [key]: option });
                        setOpenKey(null);
                      }}
                    >
                      <span>{display(option)}</span>
                      {active && <CheckIcon size={13} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
